import React, { useCallback, useEffect, useState } from 'react';
import { Card, Tabs, Form, Input, Button, Space, Switch, message, Spin, Alert, Typography, Tag } from 'antd';
import { adminWebsiteContentService } from '../../services/adminService';

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

type WebsiteContentModule = {
  id: string;
  module_key: string;
  title: string;
  content: any;
  is_published: boolean;
};

const defaultFaqContent = {
  hero: {
    eyebrow: 'FREQUENTLY ASKED QUESTIONS',
    title: 'Help & Information',
    subtitle: 'Everything you need to know before and during your stay.',
    image: 'https://images.unsplash.com/photo-1559494007-9f5847c49d94?auto=format&fit=crop&w=2200&q=80',
  },
  categories: [],
  cta: {
    title: 'Still have a question?',
    description: 'Our concierge team is available 24/7 to assist you personally.',
    buttonText: 'Contact Us',
    buttonLink: '/contact',
  },
};

const defaultContactContent = {
  hero: {
    eyebrow: 'CONNECT WITH US',
    title: 'Contact Us',
    subtitle: 'For bookings and enquiries, please call us or write to us.',
    image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=2200&q=80',
  },
  infoCard: {
    kicker: 'CONTACT INFORMATION',
    title: 'CozyStay Pacific Hotel',
    addressLines: ['572 - 636 Victoria Parade', 'P.O. Box 19217', 'Suva, Fiji'],
    phone: '+679 345 67 88',
    email: 'info@cozystay.com',
    departments: [],
  },
  contactFaqs: [],
};

const defaultAboutContent = {
  faq: {
    eyebrow: 'HAVE QUESTIONS?',
    title: 'Frequently Asked Questions',
    items: [],
  },
};

const WebsiteContentManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [faqForm] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [aboutForm] = Form.useForm();
  const [galleryForm] = Form.useForm();
  const [customPhotos, setCustomPhotos] = useState<{ src: string; alt: string; category: string; isPinned?: boolean }[]>([]);
  const [categories, setCategories] = useState<string[]>(['rooms', 'dining', 'activities', 'nature', 'other']);
  const [newCategoryInput, setNewCategoryInput] = useState('');

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);

      const [faqRes, contactRes, aboutRes, galleryRes] = await Promise.all([
        adminWebsiteContentService.getModule('faq_page').catch(() => ({ data: null })),
        adminWebsiteContentService.getModule('contact_page').catch(() => ({ data: null })),
        adminWebsiteContentService.getModule('about_page').catch(() => ({ data: null })),
        adminWebsiteContentService.getModule('gallery_page').catch(() => ({ data: null })),
      ]);

      const faqData = (faqRes.data as WebsiteContentModule) || null;
      const contactData = (contactRes.data as WebsiteContentModule) || null;
      const aboutData = (aboutRes.data as WebsiteContentModule) || null;
      const galleryData = (galleryRes.data as WebsiteContentModule) || null;

      const faqContent = faqData?.content || defaultFaqContent;
      const contactContent = contactData?.content || defaultContactContent;
      const aboutContent = aboutData?.content || defaultAboutContent;
      const galleryContent = galleryData?.content || { hero: {}, categories: ['rooms', 'dining', 'activities', 'nature', 'other'], customPhotos: [] };

      faqForm.setFieldsValue({
        title: faqData?.title || 'FAQ Page Content',
        is_published: faqData?.is_published ?? true,
        hero_eyebrow: faqContent.hero?.eyebrow,
        hero_title: faqContent.hero?.title,
        hero_subtitle: faqContent.hero?.subtitle,
        hero_image: faqContent.hero?.image,
        categories_json: JSON.stringify(faqContent.categories || [], null, 2),
        cta_title: faqContent.cta?.title,
        cta_description: faqContent.cta?.description,
        cta_button_text: faqContent.cta?.buttonText,
        cta_button_link: faqContent.cta?.buttonLink,
      });

      contactForm.setFieldsValue({
        title: contactData?.title || 'Contact Page Content',
        is_published: contactData?.is_published ?? true,
        hero_eyebrow: contactContent.hero?.eyebrow,
        hero_title: contactContent.hero?.title,
        hero_subtitle: contactContent.hero?.subtitle,
        hero_image: contactContent.hero?.image,
        info_kicker: contactContent.infoCard?.kicker,
        info_title: contactContent.infoCard?.title,
        info_address: Array.isArray(contactContent.infoCard?.addressLines)
          ? contactContent.infoCard.addressLines.join('\n')
          : '',
        info_phone: contactContent.infoCard?.phone,
        info_email: contactContent.infoCard?.email,
        departments_json: JSON.stringify(contactContent.infoCard?.departments || [], null, 2),
        faqs_json: JSON.stringify(contactContent.contactFaqs || [], null, 2),
      });

      aboutForm.setFieldsValue({
        title: aboutData?.title || 'About Page Content',
        is_published: aboutData?.is_published ?? true,
        faq_eyebrow: (aboutContent as any).faq?.eyebrow,
        faq_title: (aboutContent as any).faq?.title,
        faq_items_json: JSON.stringify((aboutContent as any).faq?.items || [], null, 2),
      });

      galleryForm.setFieldsValue({
        title: galleryData?.title || 'Gallery Page Content',
        is_published: galleryData?.is_published ?? true,
        hero_eyebrow: galleryContent.hero?.eyebrow || 'PHOTO GALLERY',
        hero_title: galleryContent.hero?.title || 'Discover the Beauty of Adam’s Palm Resort',
        hero_subtitle: galleryContent.hero?.subtitle || 'Explore beautiful resort surroundings, luxury accommodations, tropical landscapes, and relaxing spaces designed to create unforgettable memories.',
      });

      setCustomPhotos(galleryContent.customPhotos || []);
      setCategories(galleryContent.categories || ['rooms', 'dining', 'activities', 'nature', 'other']);
    } catch (error) {
      console.error('Failed to load website content modules:', error);
      message.error('Failed to load website content modules');
    } finally {
      setLoading(false);
    }
  }, [aboutForm, contactForm, faqForm, galleryForm]);

  useEffect(() => {
    void loadModules();
  }, [loadModules]);

  const saveFaqContent = async (values: any) => {
    try {
      setLoading(true);
      const categories = values.categories_json ? JSON.parse(values.categories_json) : [];

      await adminWebsiteContentService.updateModule('faq_page', {
        title: values.title,
        is_published: values.is_published,
        content: {
          hero: {
            eyebrow: values.hero_eyebrow,
            title: values.hero_title,
            subtitle: values.hero_subtitle,
            image: values.hero_image,
          },
          categories,
          cta: {
            title: values.cta_title,
            description: values.cta_description,
            buttonText: values.cta_button_text,
            buttonLink: values.cta_button_link,
          },
        },
      });

      message.success('FAQ content updated successfully');
      await loadModules();
    } catch (error) {
      console.error('Failed to save FAQ content:', error);
      message.error('Failed to save FAQ content. Check JSON fields for valid syntax.');
    } finally {
      setLoading(false);
    }
  };

  const saveContactContent = async (values: any) => {
    try {
      setLoading(true);
      const departments = values.departments_json ? JSON.parse(values.departments_json) : [];
      const contactFaqs = values.faqs_json ? JSON.parse(values.faqs_json) : [];

      await adminWebsiteContentService.updateModule('contact_page', {
        title: values.title,
        is_published: values.is_published,
        content: {
          hero: {
            eyebrow: values.hero_eyebrow,
            title: values.hero_title,
            subtitle: values.hero_subtitle,
            image: values.hero_image,
          },
          infoCard: {
            kicker: values.info_kicker,
            title: values.info_title,
            addressLines: String(values.info_address || '')
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0),
            phone: values.info_phone,
            email: values.info_email,
            departments,
          },
          contactFaqs,
        },
      });

      message.success('Contact content updated successfully');
      await loadModules();
    } catch (error) {
      console.error('Failed to save Contact content:', error);
      message.error('Failed to save Contact content. Check JSON fields for valid syntax.');
    } finally {
      setLoading(false);
    }
  };

  const saveAboutContent = async (values: any) => {
    try {
      setLoading(true);
      const faqItems = values.faq_items_json ? JSON.parse(values.faq_items_json) : [];

      await adminWebsiteContentService.updateModule('about_page', {
        title: values.title,
        is_published: values.is_published,
        content: {
          faq: {
            eyebrow: values.faq_eyebrow,
            title: values.faq_title,
            items: faqItems,
          },
        },
      });

      message.success('About page FAQ content updated successfully');
      await loadModules();
    } catch (error) {
      console.error('Failed to save About page content:', error);
      message.error('Failed to save About page content. Check JSON fields for valid syntax.');
    } finally {
      setLoading(false);
    }
  };

  const saveGalleryContent = async (values: any) => {
    try {
      setLoading(true);
      await adminWebsiteContentService.updateModule('gallery_page', {
        title: values.title,
        is_published: values.is_published,
        content: {
          hero: {
            eyebrow: values.hero_eyebrow,
            title: values.hero_title,
            subtitle: values.hero_subtitle,
          },
          categories,
          customPhotos,
        },
      });
      message.success('Gallery page content updated successfully');
      await loadModules();
    } catch (error) {
      console.error('Failed to save Gallery page content:', error);
      message.error('Failed to save Gallery page content');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryPhotoUpload = async (file: File) => {
    try {
      setLoading(true);
      const response = await adminWebsiteContentService.uploadImage(file);
      const imageUrl = String((response.data as { image_url?: string })?.image_url || '');

      if (!imageUrl) {
        message.error('Upload succeeded but no image URL was returned');
        return;
      }

      setCustomPhotos((prev) => [
        ...prev,
        {
          src: imageUrl,
          alt: file.name.split('.')[0] || 'Gallery Photo',
          category: 'other',
        },
      ]);
      message.success('Gallery photo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload gallery photo:', error);
      message.error('Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handleWebsiteImageUpload = async (formKey: 'faq' | 'contact' | 'gallery', fieldName: string, file: File) => {
    try {
      setLoading(true);
      const response = await adminWebsiteContentService.uploadImage(file);
      const imageUrl = String((response.data as { image_url?: string })?.image_url || '');

      if (!imageUrl) {
        message.error('Upload succeeded but no image URL was returned');
        return;
      }

      if (formKey === 'faq') {
        faqForm.setFieldValue(fieldName, imageUrl);
      } else if (formKey === 'contact') {
        contactForm.setFieldValue(fieldName, imageUrl);
      } else if (formKey === 'gallery') {
        galleryForm.setFieldValue(fieldName, imageUrl);
      }

      message.success('Image uploaded successfully');
    } catch (error) {
      console.error('Failed to upload website content image:', error);
      message.error('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card title="Website Content Manager" bordered={false}>
        <Paragraph type="secondary">
          Manage API-driven content blocks for public website pages. JSON fields support arrays/objects and are validated on save.
        </Paragraph>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="JSON structure tips"
          description={
            <div>
              <Text>FAQ categories format: </Text>
              <Text code>[{"{"}"label":"Category", "items":[{"{"}"question":"...", "answer":"..."{"}"}] {"}"}]</Text>
              <br />
              <Text>Departments format: </Text>
              <Text code>[{"{"}"name":"Accommodation", "detailsLines":["Line 1","Line 2"]{"}"}]</Text>
            </div>
          }
        />

        {loading ? <Spin /> : null}

        <Tabs
          items={[
            {
              key: 'faq',
              label: 'FAQ Page Content',
              children: (
                <Form layout="vertical" form={faqForm} onFinish={saveFaqContent}>
                  <Form.Item name="title" label="Module Title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="is_published" label="Published" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="hero_eyebrow" label="Hero Eyebrow"><Input /></Form.Item>
                  <Form.Item name="hero_title" label="Hero Title"><Input /></Form.Item>
                  <Form.Item name="hero_subtitle" label="Hero Subtitle"><Input /></Form.Item>
                  <Form.Item name="hero_image" label="Hero Image URL"><Input /></Form.Item>
                  <Form.Item label="Upload Hero Image">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (event) => {
                        const input = event.target as HTMLInputElement;
                        const file = input.files?.[0];
                        if (!file) return;
                        await handleWebsiteImageUpload('faq', 'hero_image', file);
                        input.value = '';
                      }}
                    />
                  </Form.Item>

                  <Form.Item name="categories_json" label="FAQ Categories (JSON)" rules={[{ required: true }]}>
                    <TextArea rows={12} />
                  </Form.Item>

                  <Form.Item name="cta_title" label="CTA Title"><Input /></Form.Item>
                  <Form.Item name="cta_description" label="CTA Description"><Input /></Form.Item>
                  <Form.Item name="cta_button_text" label="CTA Button Text"><Input /></Form.Item>
                  <Form.Item name="cta_button_link" label="CTA Button Link"><Input /></Form.Item>

                  <Space>
                    <Button type="primary" htmlType="submit">Save FAQ Content</Button>
                  </Space>
                </Form>
              ),
            },
            {
              key: 'contact',
              label: 'Contact Page Content',
              children: (
                <Form layout="vertical" form={contactForm} onFinish={saveContactContent}>
                  <Form.Item name="title" label="Module Title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="is_published" label="Published" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="hero_eyebrow" label="Hero Eyebrow"><Input /></Form.Item>
                  <Form.Item name="hero_title" label="Hero Title"><Input /></Form.Item>
                  <Form.Item name="hero_subtitle" label="Hero Subtitle"><Input /></Form.Item>
                  <Form.Item name="hero_image" label="Hero Image URL"><Input /></Form.Item>
                  <Form.Item label="Upload Hero Image">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (event) => {
                        const input = event.target as HTMLInputElement;
                        const file = input.files?.[0];
                        if (!file) return;
                        await handleWebsiteImageUpload('contact', 'hero_image', file);
                        input.value = '';
                      }}
                    />
                  </Form.Item>

                  <Form.Item name="info_kicker" label="Info Card Kicker"><Input /></Form.Item>
                  <Form.Item name="info_title" label="Info Card Title"><Input /></Form.Item>
                  <Form.Item name="info_address" label="Address Lines (one per line)"><TextArea rows={4} /></Form.Item>
                  <Form.Item name="info_phone" label="Main Phone"><Input /></Form.Item>
                  <Form.Item name="info_email" label="Main Email"><Input /></Form.Item>

                  <Form.Item name="departments_json" label="Departments (JSON)">
                    <TextArea rows={10} />
                  </Form.Item>

                  <Form.Item name="faqs_json" label="Contact FAQs (JSON)">
                    <TextArea rows={10} />
                  </Form.Item>

                  <Space>
                    <Button type="primary" htmlType="submit">Save Contact Content</Button>
                  </Space>
                </Form>
              ),
            },
            {
              key: 'about',
              label: 'About Page FAQ',
              children: (
                <Form layout="vertical" form={aboutForm} onFinish={saveAboutContent}>
                  <Form.Item name="title" label="Module Title" rules={[{ required: true }]}> 
                    <Input />
                  </Form.Item>
                  <Form.Item name="is_published" label="Published" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="faq_eyebrow" label="FAQ Eyebrow"><Input /></Form.Item>
                  <Form.Item name="faq_title" label="FAQ Title"><Input /></Form.Item>
                  <Form.Item
                    name="faq_items_json"
                    label="FAQ Items (JSON)"
                    rules={[{ required: true }]}
                    extra='Format: [{"question":"...", "answer":"..."}]'
                  >
                    <TextArea rows={12} />
                  </Form.Item>

                  <Space>
                    <Button type="primary" htmlType="submit">Save About FAQ Content</Button>
                  </Space>
                </Form>
              ),
            },
            {
              key: 'gallery',
              label: 'Gallery Content',
              children: (
                <Form layout="vertical" form={galleryForm} onFinish={saveGalleryContent}>
                  <Form.Item name="title" label="Module Title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="is_published" label="Published" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="hero_eyebrow" label="Hero Eyebrow"><Input /></Form.Item>
                  <Form.Item name="hero_title" label="Hero Title"><Input /></Form.Item>
                  <Form.Item name="hero_subtitle" label="Hero Subtitle"><Input /></Form.Item>

                  {/* Dynamic Category Management Section */}
                  <div style={{ background: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #f0f0f0', marginTop: 24, marginBottom: 24 }}>
                    <Typography.Title level={4}>Manage Gallery Filter Categories</Typography.Title>
                    <Paragraph type="secondary">
                      Add new custom filter categories for your photos or remove existing ones. Default system categories (rooms, dining, activities) cannot be removed.
                    </Paragraph>
                    <div style={{ marginBottom: 16 }}>
                      {categories.map((cat) => {
                        const isDefault = ['rooms', 'dining', 'activities'].includes(cat);
                        return (
                          <Tag
                            key={cat}
                            color={isDefault ? 'default' : 'blue'}
                            closable={!isDefault}
                            onClose={() => {
                              setCategories((prev) => prev.filter((c) => c !== cat));
                            }}
                            style={{ padding: '4px 10px', fontSize: 13, marginBottom: 8, borderRadius: '4px' }}
                          >
                            {cat.toUpperCase()}
                          </Tag>
                        );
                      })}
                    </div>
                    <Space direction="horizontal" style={{ width: '100%' }}>
                      <Input
                        value={newCategoryInput}
                        onChange={(e) => setNewCategoryInput(e.target.value)}
                        placeholder="New category name (e.g. gardens)"
                        style={{ width: 260 }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const trimmed = newCategoryInput.trim().toLowerCase();
                          if (!trimmed) return;
                          if (categories.includes(trimmed)) {
                            message.warning('Category already exists');
                            return;
                          }
                          setCategories([...categories, trimmed]);
                          setNewCategoryInput('');
                        }}
                      >
                        Add Category
                      </Button>
                    </Space>
                  </div>

                  {/* Gallery catalog images */}
                  <div style={{ marginTop: 24, marginBottom: 24 }}>
                    <Typography.Title level={4}>Gallery Catalog Images</Typography.Title>
                    <Paragraph type="secondary">
                      Upload photos directly to the gallery catalog below. You can pin important photos to the top of the gallery, and select category tags.
                    </Paragraph>

                    {customPhotos.length === 0 ? (
                      <Alert message="No custom gallery photos uploaded yet. Use the upload button below to add photos." type="info" style={{ marginBottom: 16 }} />
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
                        {customPhotos.map((photo, index) => (
                          <Card
                            key={index}
                            cover={
                              <div style={{ height: 160, overflow: 'hidden', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                <img src={photo.src.startsWith('http') || photo.src.startsWith('/') ? photo.src : `http://localhost:5000${photo.src}`} alt={photo.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                {photo.isPinned && (
                                  <span style={{ position: 'absolute', top: 8, right: 8, background: '#1890ff', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                                    📌 Pinned to Top
                                  </span>
                                )}
                              </div>
                            }
                            actions={[
                              <Button
                                type="link"
                                danger
                                onClick={() => {
                                  setCustomPhotos((prev) => prev.filter((_, i) => i !== index));
                                }}
                              >
                                Remove Image
                              </Button>
                            ]}
                            bodyStyle={{ padding: 12 }}
                          >
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>Alt Text:</Text>
                                <Input
                                  value={photo.alt}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCustomPhotos((prev) =>
                                      prev.map((item, i) => (i === index ? { ...item, alt: val } : item))
                                    );
                                  }}
                                  placeholder="e.g. Swimming Pool"
                                />
                              </div>
                              <div>
                                <Text strong>Category Filter Tag:</Text>
                                <select
                                  value={photo.category}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCustomPhotos((prev) =>
                                      prev.map((item, i) => (i === index ? { ...item, category: val } : item))
                                    );
                                  }}
                                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #d9d9d9', borderRadius: 4 }}
                                >
                                  {categories.map((cat) => (
                                    <option key={cat} value={cat}>
                                      {cat.toUpperCase()}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
                                <Switch
                                  checked={photo.isPinned || false}
                                  onChange={(checked) => {
                                    setCustomPhotos((prev) =>
                                      prev.map((item, i) => (i === index ? { ...item, isPinned: checked } : item))
                                    );
                                  }}
                                  size="small"
                                />
                                <Text style={{ marginLeft: 8 }} strong>Pin to Top</Text>
                              </div>
                            </Space>
                          </Card>
                        ))}
                      </div>
                    )}

                    <div style={{ background: '#fafafa', border: '1px dashed #d9d9d9', padding: 24, textAlign: 'center', borderRadius: 4 }}>
                      <p style={{ marginBottom: 12 }}><Text strong>Upload Photo to Gallery Catalog:</Text></p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (event) => {
                          const input = event.target as HTMLInputElement;
                          const file = input.files?.[0];
                          if (!file) return;
                          await handleGalleryPhotoUpload(file);
                          input.value = '';
                        }}
                      />
                    </div>
                  </div>

                  <Space>
                    <Button type="primary" htmlType="submit">Save Gallery Content</Button>
                  </Space>
                </Form>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default WebsiteContentManager;
